import { Fragment, useEffect, useMemo, useState } from 'react'
import { useLocation, useNavigate, useSearchParams } from 'react-router-dom'
import AppPagination from '../../../components/AppPagination'
import AppDateInput from '../../../components/AppDateInput'
import { resolveMediaUrl } from '../../../utils/mediaUrl'
import { listTimesheets } from '../timesheet/api/timesheetApi'
import SecureField from '../../../components/SecureField'
import { parseStoredUser } from '../../../utils/authSession'
import {
  getProjectDrilldown,
  getProjectProfitability,
  recalculateProjectProfitability,
  getProjectAmcSchedules,
  receiveAmcCyclePayment,
} from './api/projectApi'
import { appToast } from '../../../components/AppToast'

const SECTION_PAGE_SIZE = 10

function formatDate(value) {
  if (!value) return '-'
  const date = new Date(value)
  if (Number.isNaN(date.getTime())) return '-'
  return date.toLocaleDateString()
}

function badgeClass(status) {
  const key = String(status || '').toUpperCase()
  if (key === 'COMPLETED' || key === 'APPROVED') return 'bg-emerald-100 text-emerald-700'
  if (key === 'IN_PROGRESS' || key === 'PENDING') return 'bg-amber-100 text-amber-700'
  if (key === 'REJECTED') return 'bg-rose-100 text-rose-700'
  if (key === 'CLOSED') return 'bg-slate-200 text-slate-700'
  return 'bg-amber-100 text-amber-700'
}

function compactHours(value) {
  const num = Number(value || 0)
  if (!Number.isFinite(num) || num <= 0) return '-'
  return `${num.toFixed(1)}h`
}



export default function ProjectDetailScreen() {
  const navigate = useNavigate()
  const location = useLocation()
  const [searchParams] = useSearchParams()
  const projectId = Number(searchParams.get('id') || 0)
  const [state, setState] = useState({
    loading: true,
    error: '',
    payload: null,
  })
  const [taskPage, setTaskPage] = useState(1)
  const [jobPage, setJobPage] = useState(1)
  const [timesheetPage, setTimesheetPage] = useState(1)

  const { isAdmin: _isAdmin, privileges: _privileges } = useMemo(() => parseStoredUser(), [])
  const canViewFinancials = _isAdmin || _privileges.includes('ProjectFinancialsView')
  const canViewCustomer360 = canViewFinancials && (_isAdmin || _privileges.includes('CustomerProfitabilityView'))
  const canViewAmcSchedule = canViewFinancials && (_isAdmin || _privileges.includes('AmcBillingScheduleView'))
  const canManageAmcPayments = canViewAmcSchedule && (_isAdmin || _privileges.includes('AmcBillingScheduleCreate'))

  const [activeTab, setActiveTab] = useState('drilldown')

  useEffect(() => {
    if (activeTab === 'customer360' && !canViewCustomer360) {
      setActiveTab('drilldown')
    }
    if (activeTab === 'amc_schedule' && !canViewAmcSchedule) {
      setActiveTab('drilldown')
    }
  }, [activeTab, canViewCustomer360, canViewAmcSchedule])
  const [preset, setPreset] = useState('ALL')
  const [fromDate, setFromDate] = useState('')
  const [toDate, setToDate] = useState('')
  const [profitability, setProfitability] = useState(null)
  const [profitabilityLoading, setProfitabilityLoading] = useState(false)
  const [profitabilityError, setProfitabilityError] = useState('')
  const [expandedEmployeeId, setExpandedEmployeeId] = useState(null)
  const [selectedAssigneeId, setSelectedAssigneeId] = useState(null)
  // Timesheets loaded per job for this project
  const [allTimesheets, setAllTimesheets] = useState([])
  const [timesheetsLoading, setTimesheetsLoading] = useState(false)

  // AMC Schedule state
  const [amcSchedules, setAmcSchedules] = useState([])
  const [loadingAmcSchedules, setLoadingAmcSchedules] = useState(false)
  const [amcScheduleError, setAmcScheduleError] = useState('')

  // Receive Payment Modal state
  const [payModalSchedule, setPayModalSchedule] = useState(null)
  const [payAmount, setPayAmount] = useState('')
  const [payDate, setPayDate] = useState('')
  const [payNotes, setPayNotes] = useState('')
  const [isSubmittingPay, setIsSubmittingPay] = useState(false)

  const [sortField, setSortField] = useState('hoursLogged')
  const [sortDirection, setSortDirection] = useState('desc')

  const project = state.payload?.project || {}

  const fetchAmcSchedules = async () => {
    if (!projectId) return
    setLoadingAmcSchedules(true)
    try {
      const data = await getProjectAmcSchedules(projectId)
      setAmcSchedules(Array.isArray(data) ? data : [])
      setAmcScheduleError('')
    } catch (err) {
      console.error(err)
      setAmcScheduleError('Failed to load AMC billing schedule.')
    } finally {
      setLoadingAmcSchedules(false)
    }
  }

  useEffect(() => {
    if (projectId && canViewAmcSchedule && (activeTab === 'amc_schedule' || project.project_billing_mode === 'AMC')) {
      fetchAmcSchedules()
    }
  }, [projectId, activeTab, project.project_billing_mode, canViewAmcSchedule])

  const handleOpenReceivePayment = (sched) => {
    setPayModalSchedule(sched)
    setPayAmount(String(sched.amount || ''))
    setPayDate(new Date().toISOString().slice(0, 10))
    setPayNotes('')
  }

  const handleSubmitReceivePayment = async (e) => {
    e.preventDefault()
    if (!payModalSchedule) return
    setIsSubmittingPay(true)
    try {
      await receiveAmcCyclePayment(payModalSchedule.id, {
        amount_received: Number(payAmount),
        payment_date: payDate,
        reference_notes: payNotes,
      })
      appToast.success(`Payment of ₹${Number(payAmount).toLocaleString()} recorded successfully!`)
      setPayModalSchedule(null)
      fetchAmcSchedules()
      if (projectId) {
        getProjectDrilldown(projectId).then((res) => setState((prev) => ({ ...prev, payload: res })))
      }
    } catch (err) {
      console.error(err)
      appToast.error(err.response?.data?.detail || 'Failed to record AMC payment.')
    } finally {
      setIsSubmittingPay(false)
    }
  }

  function handleSort(field) {
    if (sortField === field) {
      setSortDirection((prev) => (prev === 'asc' ? 'desc' : 'asc'))
    } else {
      setSortField(field)
      setSortDirection('desc')
    }
  }
  const summary = state.payload?.summary || {}

  function handlePresetChange(selected) {
    setPreset(selected)
    const today = new Date()
    const currentYear = today.getFullYear()

    if (selected === 'ALL') {
      const projStart = project.start_date ? project.start_date.slice(0, 10) : ''
      // Use the project's end_date (or AMC end_date) as the upper bound for ALL so the
      // full contract lifetime revenue is shown, not just revenue up to today.
      const projEnd =
        project.end_date
          ? project.end_date.slice(0, 10)
          : project.project_amc_end_date
          ? project.project_amc_end_date.slice(0, 10)
          : today.toISOString().slice(0, 10)
      setFromDate(projStart)
      setToDate(projEnd)
    } else if (selected === 'THIS_YEAR') {
      setFromDate(`${currentYear}-01-01`)
      setToDate(`${currentYear}-12-31`)
    } else if (selected === 'LAST_YEAR') {
      setFromDate(`${currentYear - 1}-01-01`)
      setToDate(`${currentYear - 1}-12-31`)
    } else if (selected === 'LAST_6_MONTHS') {
      const sixMonthsAgo = new Date()
      sixMonthsAgo.setMonth(today.getMonth() - 6)
      setFromDate(sixMonthsAgo.toISOString().slice(0, 10))
      setToDate(today.toISOString().slice(0, 10))
    }
    // Editing date inputs directly will also clear the preset to 'CUSTOM'
  }

  function handleFromDateChange(val) {
    setFromDate(val)
    setPreset('CUSTOM')
  }

  function handleToDateChange(val) {
    setToDate(val)
    setPreset('CUSTOM')
  }

  async function handleRecalculate() {
    if (!projectId || !project.billable) return
    setProfitabilityLoading(true)
    setProfitabilityError('')
    try {
      const params = {}
      if (fromDate) params.fromDate = fromDate
      if (toDate) params.toDate = toDate
      await recalculateProjectProfitability(projectId)
      const data = await getProjectProfitability(projectId, params)
      setProfitability(data)
    } catch (err) {
      setProfitabilityError(err.response?.data?.detail || err.message || 'Failed to reload profitability.')
    } finally {
      setProfitabilityLoading(false)
    }
  }


  useEffect(() => {
    let active = true
    async function load() {
      if (!projectId) {
        setState({ loading: false, error: 'Invalid project id.', payload: null })
        return
      }
      setState((prev) => ({ ...prev, loading: true, error: '' }))
      try {
        const payload = await getProjectDrilldown(projectId)
        if (!active) return
        setState({ loading: false, error: '', payload })
      } catch (error) {
        if (!active) return
        setState({
          loading: false,
          error: error?.response?.data?.detail || 'Failed to load project drilldown.',
          payload: null,
        })
      }
    }
    load()
    return () => {
      active = false
    }
  }, [projectId])

  useEffect(() => {
    setTaskPage(1)
    setJobPage(1)
    setTimesheetPage(1)
    setSelectedAssigneeId(null)
  }, [projectId])

  // Initialise the date pickers from the project's own start / end dates once
  // the project data has been loaded so the first profitability API call uses
  // the correct full-lifetime range (mirrors what ALL preset produces).
  useEffect(() => {
    if (!project.id) return
    const projStart = project.start_date ? project.start_date.slice(0, 10) : ''
    const projEnd =
      project.end_date
        ? project.end_date.slice(0, 10)
        : project.project_amc_end_date
        ? project.project_amc_end_date.slice(0, 10)
        : new Date().toISOString().slice(0, 10)
    setFromDate(projStart)
    setToDate(projEnd)
    setPreset('ALL')
  // Only re-run when the project identity changes (new project opened).
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [project.id])

  useEffect(() => {
    if (!projectId || !project.billable || !canViewCustomer360) return
    let active = true

    async function fetchProfit() {
      setProfitabilityLoading(true)
      setProfitabilityError('')
      try {
        const params = {}
        if (fromDate) params.fromDate = fromDate
        if (toDate) params.toDate = toDate
        const data = await getProjectProfitability(projectId, params)
        if (active) {
          setProfitability(data)
        }
      } catch (err) {
        if (active) {
          setProfitabilityError(err.response?.data?.detail || err.message || 'Failed to load profitability details.')
        }
      } finally {
        if (active) {
          setProfitabilityLoading(false)
        }
      }
    }

    fetchProfit()
    return () => {
      active = false
    }
  }, [projectId, fromDate, toDate, project.billable])

  const assigneeBreakdown = useMemo(
    () => (Array.isArray(state.payload?.assigneeBreakdown) ? state.payload.assigneeBreakdown : []),
    [state.payload?.assigneeBreakdown],
  )
  const allTasks = useMemo(() => (Array.isArray(state.payload?.tasks) ? state.payload.tasks : []), [state.payload?.tasks])
  const allJobs = useMemo(() => (Array.isArray(state.payload?.jobs) ? state.payload.jobs : []), [state.payload?.jobs])

  // Fetch all timesheets for this project directly
  useEffect(() => {
    let active = true
    async function fetchTimesheets() {
      if (!projectId) { setAllTimesheets([]); return }
      setTimesheetsLoading(true)
      try {
        const res = await listTimesheets({
          project_id: projectId,
          page: 1,
          page_size: 500,
          fromDate: '1900-01-01',
          toDate: '9999-12-31',
          archive: false,
        })
        if (!active) return
        const items = Array.isArray(res?.items) ? res.items : Array.isArray(res) ? res : []
        setAllTimesheets(items)
      } catch (err) {
        console.error('Failed to load project timesheets', err)
        if (active) setAllTimesheets([])
      } finally {
        if (active) setTimesheetsLoading(false)
      }
    }
    fetchTimesheets()
    return () => { active = false }
  }, [projectId])

  const assigneeNameById = useMemo(() => {
    const map = new Map()
    for (const item of assigneeBreakdown) {
      const userId = Number(item?.userId || 0)
      if (userId > 0) map.set(userId, item?.name || `User #${userId}`)
    }
    return map
  }, [assigneeBreakdown])

  const sortedEmployeeBreakdown = useMemo(() => {
    const list = [...(profitability?.project?.employeeBreakdown || [])]
    if (!sortField) return list
    return list.sort((a, b) => {
      let aVal = a[sortField]
      let bVal = b[sortField]
      
      if (sortField === 'employeeCost') {
        aVal = a.employeeCost || 0
        bVal = b.employeeCost || 0
      } else if (sortField === 'hoursLogged') {
        aVal = a.hoursLogged || 0
        bVal = b.hoursLogged || 0
      }
      
      if (typeof aVal === 'string') {
        return sortDirection === 'asc'
          ? aVal.localeCompare(bVal)
          : bVal.localeCompare(aVal)
      } else {
        return sortDirection === 'asc'
          ? (aVal || 0) - (bVal || 0)
          : (bVal || 0) - (aVal || 0)
      }
    })
  }, [profitability?.project?.employeeBreakdown, sortField, sortDirection])

  // When assignee filter changes, reset pages
  useEffect(() => {
    setTaskPage(1)
    setJobPage(1)
    setTimesheetPage(1)
  }, [selectedAssigneeId])

  // Filtered lists by selected assignee
  const filteredTasks = useMemo(() => {
    if (!selectedAssigneeId) return allTasks
    return allTasks.filter((t) =>
      Array.isArray(t.assigneeUserIds)
        ? t.assigneeUserIds.map(Number).includes(selectedAssigneeId)
        : Number(t.assigneeUserId) === selectedAssigneeId
    )
  }, [allTasks, selectedAssigneeId])

  const filteredJobs = useMemo(() => {
    if (!selectedAssigneeId) return allJobs
    return allJobs.filter((j) => Number(j.assigneeUserId) === selectedAssigneeId)
  }, [allJobs, selectedAssigneeId])

  const filteredTimesheets = useMemo(() => {
    if (!selectedAssigneeId) return allTimesheets
    return allTimesheets.filter(
      (ts) => Number(ts.userId || ts.user_id || ts.employee_id || ts.employeeId) === selectedAssigneeId
    )
  }, [allTimesheets, selectedAssigneeId])

  const taskTotal = filteredTasks.length
  const jobTotal = filteredJobs.length
  const timesheetTotal = filteredTimesheets.length
  const taskTotalPages = Math.max(1, Math.ceil(taskTotal / SECTION_PAGE_SIZE))
  const jobTotalPages = Math.max(1, Math.ceil(jobTotal / SECTION_PAGE_SIZE))
  const timesheetTotalPages = Math.max(1, Math.ceil(timesheetTotal / SECTION_PAGE_SIZE))
  const pagedTasks = useMemo(() => {
    const start = (taskPage - 1) * SECTION_PAGE_SIZE
    return filteredTasks.slice(start, start + SECTION_PAGE_SIZE)
  }, [filteredTasks, taskPage])
  const pagedJobs = useMemo(() => {
    const start = (jobPage - 1) * SECTION_PAGE_SIZE
    return filteredJobs.slice(start, start + SECTION_PAGE_SIZE)
  }, [filteredJobs, jobPage])
  const pagedTimesheets = useMemo(() => {
    const start = (timesheetPage - 1) * SECTION_PAGE_SIZE
    return filteredTimesheets.slice(start, start + SECTION_PAGE_SIZE)
  }, [filteredTimesheets, timesheetPage])

  const taskRowLabel = useMemo(() => {
    if (!pagedTasks.length) return 'Showing 0 - 0 of 0 tasks'
    const start = (taskPage - 1) * SECTION_PAGE_SIZE + 1
    const end = Math.min(start + pagedTasks.length - 1, taskTotal)
    return `Showing ${start} - ${end} of ${taskTotal} tasks`
  }, [pagedTasks.length, taskPage, taskTotal])

  const jobRowLabel = useMemo(() => {
    if (!pagedJobs.length) return 'Showing 0 - 0 of 0 jobs'
    const start = (jobPage - 1) * SECTION_PAGE_SIZE + 1
    const end = Math.min(start + pagedJobs.length - 1, jobTotal)
    return `Showing ${start} - ${end} of ${jobTotal} jobs`
  }, [pagedJobs.length, jobPage, jobTotal])

  const timesheetRowLabel = useMemo(() => {
    if (!pagedTimesheets.length) return 'Showing 0 - 0 of 0 timesheets'
    const start = (timesheetPage - 1) * SECTION_PAGE_SIZE + 1
    const end = Math.min(start + pagedTimesheets.length - 1, timesheetTotal)
    return `Showing ${start} - ${end} of ${timesheetTotal} timesheets`
  }, [pagedTimesheets.length, timesheetPage, timesheetTotal])

  function taskResponsibleLabel(row) {
    const ids = Array.isArray(row?.assigneeUserIds) ? row.assigneeUserIds : []
    if (!ids.length) return '-'
    const names = ids.map((rawId) => {
      const userId = Number(rawId || 0)
      if (userId <= 0) return 'Unknown'
      return assigneeNameById.get(userId) || `User #${userId}`
    })
    return names.join(', ')
  }

  function jobResponsibleLabel(row) {
    const userId = Number(row?.assigneeUserId || 0)
    if (userId <= 0) return '-'
    return assigneeNameById.get(userId) || `User #${userId}`
  }

  const customer360 = profitability?.customer360 || {}
  const ticketMetrics = customer360.tickets || {}
  const departmentEffort = Array.isArray(customer360.departmentEffort) ? customer360.departmentEffort : []
  const ticketTimeline = Array.isArray(customer360.timeline) ? customer360.timeline : []

  const timelineEvents = useMemo(() => {
    if (ticketTimeline.length) {
      return ticketTimeline
        .map((evt) => {
          const eventType = String(evt.eventType || '').toUpperCase()
          const ticketLabel = evt.ticketNo || evt.ticketId || evt.taskCode || evt.id
          return {
            id: `ticket-${evt.id}`,
            type: eventType === 'FEEDBACK_RESPONSE' ? 'CUSTOMER_FEEDBACK' : eventType,
            title: `${eventType || 'TICKET'}: ${ticketLabel}`,
            code: evt.taskCode || ticketLabel,
            date: evt.occurredAt,
            status: evt.syncResult,
            user: evt.syncAction || '',
          }
        })
        .filter(e => e.date)
        .sort((a, b) => new Date(b.date) - new Date(a.date))
    }

    const events = []
    allTasks.forEach(t => {
      events.push({
        id: `task-${t.id}`,
        type: 'TICKET_OPENED',
        title: `Ticket synced: ${t.taskName}`,
        code: t.taskCode,
        date: t.plannedStart,
        priority: t.priority,
        status: t.status,
      })
    })
    allJobs.forEach(j => {
      const isFeedback = /feedback|customer/i.test(j.jobName)
      events.push({
        id: `job-${j.id}`,
        type: isFeedback ? 'CUSTOMER_FEEDBACK' : 'INTERNAL_ACTIVITY',
        title: j.jobName,
        code: j.jobCode,
        date: j.plannedStart,
        status: j.status,
        user: jobResponsibleLabel(j),
      })
    })
    return events
      .filter(e => e.date)
      .sort((a, b) => new Date(b.date) - new Date(a.date))
  }, [ticketTimeline, allTasks, allJobs, assigneeNameById])

  return (
    <div className="mx-auto w-full max-w-[1320px] pb-8">
      <header className="mb-8 flex items-start justify-between gap-4">
        <div>
          <p className="mb-2 text-xs font-bold uppercase tracking-[0.16em] text-primary">Project Drilldown</p>
          <h1 className="text-4xl font-extrabold tracking-tight text-on-surface md:text-5xl">{project.name || 'Project Details'}</h1>
          <p className="mt-2 text-sm text-on-surface-variant">
            {project.project_code || '-'} | Manager: {project.manager_name || '-'} | Status: {project.status || '-'}
          </p>
        </div>

        <button
          type="button"
          className="rounded-lg border border-outline-variant/30 px-4 py-2 text-sm font-semibold text-on-surface"
          onClick={() => {
            if (location.state?.returnTo) {
              navigate(location.state.returnTo)
            } else {
              navigate('/nerve/project/list')
            }
          }}
        >
          Back to Projects
        </button>
      </header>

      {/* Tab Selectors */}
      {project.billable && (
        <div className="mb-8 border-b border-outline-variant/30 flex gap-4">
          <button
            type="button"
            className={`pb-3 text-sm font-bold border-b-2 transition-all ${activeTab === 'drilldown'
              ? 'border-primary text-primary'
              : 'border-transparent text-on-surface-variant hover:text-on-surface'
              }`}
            onClick={() => setActiveTab('drilldown')}
          >
            Operational Drilldown
          </button>
          {canViewAmcSchedule && (project.project_billing_mode === 'AMC' || Number(project.project_amc_amount || 0) > 0) && (
            <button
              type="button"
              className={`pb-3 text-sm font-bold border-b-2 transition-all ${activeTab === 'amc_schedule'
                ? 'border-primary text-primary'
                : 'border-transparent text-on-surface-variant hover:text-on-surface'
                }`}
              onClick={() => setActiveTab('amc_schedule')}
            >
              AMC Billing Schedule &amp; Payments
            </button>
          )}
          {canViewCustomer360 && (
            <button
              type="button"
              className={`pb-3 text-sm font-bold border-b-2 transition-all ${activeTab === 'customer360'
                ? 'border-primary text-primary'
                : 'border-transparent text-on-surface-variant hover:text-on-surface'
                }`}
              onClick={() => setActiveTab('customer360')}
            >
              Customer 360 &amp; Profitability
            </button>
          )}
        </div>
      )}

      {state.loading ? (
        <div className="rounded-2xl bg-surface-container-lowest p-8 text-sm text-on-surface-variant">Loading project details...</div>
      ) : null}
      {state.error ? (
        <div className="rounded-2xl border border-error/30 bg-error-container p-4 text-sm text-error">{state.error}</div>
      ) : null}

      {!state.loading && !state.error ? (
        activeTab === 'amc_schedule' ? (
          <div className="space-y-6">
            {/* Header summary card */}
            <article className="rounded-2xl bg-surface-container-lowest p-6 border border-outline-variant/15 shadow-sm">
              <div className="flex flex-col gap-4 sm:flex-row sm:items-center sm:justify-between">
                <div>
                  <span className="text-xs font-bold uppercase tracking-wider text-primary">AMC Contract Overview</span>
                  <h2 className="text-2xl font-extrabold text-on-surface mt-1">{project.name}</h2>
                  <p className="text-xs text-on-surface-variant mt-1">
                    Frequency: <span className="font-bold">{project.project_amc_frequency || 'QUARTERLY'}</span> • 
                    Per-Cycle Amount: <span className="font-bold">{project.project_amc_currency || 'INR'} {Number(project.project_amc_amount || 0).toLocaleString(undefined, { minimumFractionDigits: 2 })}</span> • 
                    Contract Start: <span className="font-bold">{formatDate(project.project_amc_start_date || project.start_date)}</span>
                  </p>
                </div>
                <div className="rounded-xl bg-primary/10 px-5 py-3 text-right">
                  <p className="text-[10px] font-bold uppercase tracking-wider text-primary">Total Cash Collected</p>
                  <p className="text-xl font-extrabold text-primary">
                    {project.project_amc_currency || 'INR'} {Number(project.total_received_amount || project.received_amount || 0).toLocaleString(undefined, { minimumFractionDigits: 2 })}
                  </p>
                </div>
              </div>
            </article>

            {/* Cycles Table */}
            <article className="rounded-2xl bg-surface-container-lowest p-6 border border-outline-variant/15 shadow-sm">
              <div className="mb-4 flex items-center justify-between">
                <div>
                  <h3 className="text-lg font-bold text-on-surface">Recurring Billing Cycles &amp; Collections</h3>
                  <p className="text-xs text-on-surface-variant">View all generated schedule cycles and record payments per cycle.</p>
                </div>
                <button
                  type="button"
                  onClick={fetchAmcSchedules}
                  className="inline-flex items-center gap-1.5 rounded-lg border border-outline px-3 py-1.5 text-xs font-bold text-on-surface transition hover:bg-surface-container-high"
                >
                  <span className="material-symbols-outlined text-sm">refresh</span>
                  Refresh Schedule
                </button>
              </div>

              {loadingAmcSchedules ? (
                <div className="py-12 text-center text-sm text-on-surface-variant">Loading recurring billing schedules...</div>
              ) : amcScheduleError ? (
                <div className="rounded-xl bg-error-container p-4 text-xs text-error">{amcScheduleError}</div>
              ) : amcSchedules.length === 0 ? (
                <div className="py-12 text-center text-sm text-on-surface-variant">No recurring billing cycles generated yet.</div>
              ) : (
                <div className="overflow-x-auto">
                  <table className="w-full text-left text-sm">
                    <thead>
                      <tr className="border-b border-outline-variant/30 text-xs font-bold uppercase tracking-wider text-on-surface-variant">
                        <th className="py-3 px-4">Cycle #</th>
                        <th className="py-3 px-4">Period Range</th>
                        <th className="py-3 px-4">Due Date</th>
                        <th className="py-3 px-4">Cycle Amount</th>
                        <th className="py-3 px-4">Status</th>
                        <th className="py-3 px-4 text-right">Action</th>
                      </tr>
                    </thead>
                    <tbody className="divide-y divide-outline-variant/15">
                      {amcSchedules.map((sched) => {
                        const isPaid = String(sched.status).toUpperCase() === 'PAID'
                        return (
                          <tr key={sched.id} className="hover:bg-surface-container-low/50">
                            <td className="py-3.5 px-4 font-mono font-bold text-on-surface">
                              Cycle #{sched.cycle_number}
                            </td>
                            <td className="py-3.5 px-4 text-xs text-on-surface-variant">
                              {formatDate(sched.period_start_date)} — {formatDate(sched.period_end_date)}
                            </td>
                            <td className="py-3.5 px-4 text-xs font-semibold text-on-surface">
                              {formatDate(sched.due_date)}
                            </td>
                            <td className="py-3.5 px-4 font-bold text-on-surface">
                              {project.project_amc_currency || 'INR'} {Number(sched.amount || 0).toLocaleString(undefined, { minimumFractionDigits: 2 })}
                            </td>
                            <td className="py-3.5 px-4">
                              <span className={`inline-flex items-center gap-1 rounded-full px-2.5 py-0.5 text-xs font-bold ${
                                isPaid ? 'bg-emerald-100 text-emerald-800' : 'bg-amber-100 text-amber-800'
                              }`}>
                                <span className="material-symbols-outlined text-xs">
                                  {isPaid ? 'check_circle' : 'pending'}
                                </span>
                                {isPaid ? 'PAID' : 'PENDING'}
                              </span>
                            </td>
                            <td className="py-3.5 px-4 text-right">
                              {!isPaid ? (
                                canManageAmcPayments ? (
                                  <button
                                    type="button"
                                    onClick={() => handleOpenReceivePayment(sched)}
                                    className="inline-flex items-center gap-1 rounded-lg bg-emerald-600 px-3 py-1.5 text-xs font-bold text-white transition hover:bg-emerald-700 shadow-sm"
                                  >
                                    <span className="material-symbols-outlined text-sm">payments</span>
                                    Receive Payment
                                  </button>
                                ) : (
                                  <span className="text-xs text-on-surface-variant/60 font-medium">Pending</span>
                                )
                              ) : (
                                <span className="text-xs font-semibold text-emerald-600">Payment Settled</span>
                              )}
                            </td>
                          </tr>
                        )
                      })}
                    </tbody>
                  </table>
                </div>
              )}
            </article>
          </div>
        ) : activeTab === 'drilldown' || (activeTab === 'customer360' && !canViewCustomer360) ? (
          <div className="space-y-6">
            <section className="grid grid-cols-1 gap-4 md:grid-cols-4">
              <article className="rounded-2xl bg-surface-container-lowest p-4">
                <p className="text-xs font-bold uppercase tracking-[0.12em] text-on-surface-variant">Overall Progress</p>
                <p className="mt-2 text-3xl font-extrabold text-primary">{Number(summary.overallProgressPct || 0).toFixed(1)}%</p>
              </article>
              <article className="rounded-2xl bg-surface-container-lowest p-4">
                <p className="text-xs font-bold uppercase tracking-[0.12em] text-on-surface-variant">Tasks</p>
                <p className="mt-2 text-3xl font-extrabold text-on-surface">{summary.taskCount || 0}</p>
                <p className="text-xs text-on-surface-variant">Completed: {summary.completedTaskCount || 0}</p>
              </article>
              <article className="rounded-2xl bg-surface-container-lowest p-4">
                <p className="text-xs font-bold uppercase tracking-[0.12em] text-on-surface-variant">Jobs</p>
                <p className="mt-2 text-3xl font-extrabold text-on-surface">{summary.jobCount || 0}</p>
                <p className="text-xs text-on-surface-variant">Completed: {summary.completedJobCount || 0}</p>
              </article>
              <article className="rounded-2xl bg-surface-container-lowest p-4">
                <p className="text-xs font-bold uppercase tracking-[0.12em] text-on-surface-variant">Timeline</p>
                <p className="mt-2 text-sm font-bold text-on-surface">
                  {formatDate(project.start_date)} - {formatDate(project.end_date)}
                </p>
              </article>
            </section>

            <section className="rounded-2xl bg-surface-container-lowest p-4">
              <div className="flex items-center justify-between">
                <h2 className="text-lg font-extrabold text-on-surface">Assignee Breakdown</h2>
                {selectedAssigneeId && (
                  <button
                    type="button"
                    onClick={() => setSelectedAssigneeId(null)}
                    className="flex items-center gap-1 rounded-lg bg-primary/10 px-3 py-1 text-xs font-bold text-primary hover:bg-primary/20"
                  >
                    <span className="material-symbols-outlined text-sm">close</span>
                    Clear filter
                  </button>
                )}
              </div>
              <p className="mt-1 text-xs text-on-surface-variant">Click an assignee to filter Tasks, Jobs &amp; Timesheets by that person.</p>
              {assigneeBreakdown.length ? (
                <div className="mt-3 grid grid-cols-1 gap-3 md:grid-cols-2 xl:grid-cols-3">
                  {assigneeBreakdown.map((row) => {
                    const isSelected = selectedAssigneeId === row.userId
                    return (
                      <article
                        key={row.userId}
                        role="button"
                        tabIndex={0}
                        onClick={() => setSelectedAssigneeId(isSelected ? null : row.userId)}
                        onKeyDown={(e) => { if (e.key === 'Enter' || e.key === ' ') setSelectedAssigneeId(isSelected ? null : row.userId) }}
                        className={`flex cursor-pointer items-center gap-3 rounded-xl p-3 transition-all select-none ${isSelected
                          ? 'bg-primary text-on-primary ring-2 ring-primary/60 shadow-md'
                          : 'bg-surface-container-low hover:bg-surface-container hover:shadow-sm'
                          }`}
                      >
                        {row.profilePhotoPath ? (
                          <img src={resolveMediaUrl(row.profilePhotoPath)} alt={row.name} className="h-10 w-10 rounded-full object-cover" />
                        ) : (
                          <div className={`flex h-10 w-10 shrink-0 items-center justify-center rounded-full text-xs font-bold ${isSelected ? 'bg-on-primary/20 text-on-primary' : 'bg-primary/10 text-primary'
                            }`}>
                            {String(row.name || 'U')
                              .split(' ')
                              .filter(Boolean)
                              .slice(0, 2)
                              .map((part) => part[0]?.toUpperCase())
                              .join('')}
                          </div>
                        )}
                        <div>
                          <p className={`text-sm font-bold ${isSelected ? 'text-on-primary' : 'text-on-surface'}`}>{row.name}</p>
                          <p className={`text-xs ${isSelected ? 'text-on-primary/80' : 'text-on-surface-variant'}`}>
                            Tasks: {row.taskCount} | Jobs: {row.jobCount}
                          </p>
                        </div>
                        {isSelected && (
                          <span className="material-symbols-outlined ml-auto text-sm text-on-primary">check_circle</span>
                        )}
                      </article>
                    )
                  })}
                </div>
              ) : (
                <p className="mt-2 text-sm text-on-surface-variant">No assignee data available.</p>
              )}
            </section>

            <section className="rounded-2xl bg-surface-container-lowest p-4">
              <h2 className="text-lg font-extrabold text-on-surface">Tasks</h2>
              <div className="mt-3 overflow-x-auto">
                <table className="w-full min-w-[900px] border-collapse text-left">
                  <thead>
                    <tr className="border-b border-surface-container">
                      <th className="px-3 py-2 text-xs font-bold uppercase tracking-[0.12em] text-on-surface-variant">Code</th>
                      <th className="px-3 py-2 text-xs font-bold uppercase tracking-[0.12em] text-on-surface-variant">Task</th>
                      <th className="px-3 py-2 text-xs font-bold uppercase tracking-[0.12em] text-on-surface-variant">Responsible Person</th>
                      <th className="px-3 py-2 text-xs font-bold uppercase tracking-[0.12em] text-on-surface-variant">Status</th>
                      <th className="px-3 py-2 text-xs font-bold uppercase tracking-[0.12em] text-on-surface-variant">Priority</th>
                      <th className="px-3 py-2 text-xs font-bold uppercase tracking-[0.12em] text-on-surface-variant">Planned</th>
                    </tr>
                  </thead>
                  <tbody className="divide-y divide-surface-container/70">
                    {pagedTasks.length ? (
                      pagedTasks.map((row) => (
                        <tr key={row.id}>
                          <td className="px-3 py-2 text-sm font-semibold text-primary">{row.taskCode || '-'}</td>
                          <td className="px-3 py-2 text-sm font-semibold text-on-surface">{row.taskName || '-'}</td>
                          <td className="px-3 py-2 text-sm text-on-surface-variant">{taskResponsibleLabel(row)}</td>
                          <td className="px-3 py-2">
                            <span className={`rounded-full px-2 py-1 text-[10px] font-bold uppercase ${badgeClass(row.status)}`}>{String(row.status || '-').replaceAll('_', ' ')}</span>
                          </td>
                          <td className="px-3 py-2 text-sm text-on-surface-variant">{row.priority || '-'}</td>
                          <td className="px-3 py-2 text-sm text-on-surface-variant">
                            {formatDate(row.plannedStart)} - {formatDate(row.plannedEnd)}
                          </td>
                        </tr>
                      ))
                    ) : (
                      <tr>
                        <td colSpan={6} className="px-3 py-5 text-sm text-on-surface-variant">No tasks found.</td>
                      </tr>
                    )}
                  </tbody>
                </table>
              </div>
              <div className="mt-4 flex items-center justify-between gap-4">
                <span className="text-xs text-on-surface-variant">{taskRowLabel}</span>
                <AppPagination
                  page={taskPage}
                  totalPages={taskTotalPages}
                  onPageChange={setTaskPage}
                  onPrevious={() => setTaskPage((prev) => Math.max(1, prev - 1))}
                  onNext={() => setTaskPage((prev) => Math.min(taskTotalPages, prev + 1))}
                  canPrevious={taskPage > 1}
                  canNext={taskPage < taskTotalPages}
                />
              </div>
            </section>

            <section className="rounded-2xl bg-surface-container-lowest p-4">
              <h2 className="text-lg font-extrabold text-on-surface">Jobs</h2>
              <div className="mt-3 overflow-x-auto">
                <table className="w-full min-w-[900px] border-collapse text-left">
                  <thead>
                    <tr className="border-b border-surface-container">
                      <th className="px-3 py-2 text-xs font-bold uppercase tracking-[0.12em] text-on-surface-variant">Code</th>
                      <th className="px-3 py-2 text-xs font-bold uppercase tracking-[0.12em] text-on-surface-variant">Job</th>
                      <th className="px-3 py-2 text-xs font-bold uppercase tracking-[0.12em] text-on-surface-variant">Responsible Person</th>
                      <th className="px-3 py-2 text-xs font-bold uppercase tracking-[0.12em] text-on-surface-variant">Status</th>
                      <th className="px-3 py-2 text-xs font-bold uppercase tracking-[0.12em] text-on-surface-variant">Planned Hours</th>
                      <th className="px-3 py-2 text-xs font-bold uppercase tracking-[0.12em] text-on-surface-variant">Planned</th>
                    </tr>
                  </thead>
                  <tbody className="divide-y divide-surface-container/70">
                    {pagedJobs.length ? (
                      pagedJobs.map((row) => (
                        <tr key={row.id}>
                          <td className="px-3 py-2 text-sm font-semibold text-primary">{row.jobCode || '-'}</td>
                          <td className="px-3 py-2 text-sm font-semibold text-on-surface">{row.jobName || '-'}</td>
                          <td className="px-3 py-2 text-sm text-on-surface-variant">{jobResponsibleLabel(row)}</td>
                          <td className="px-3 py-2">
                            <span className={`rounded-full px-2 py-1 text-[10px] font-bold uppercase ${badgeClass(row.status)}`}>{String(row.status || '-').replaceAll('_', ' ')}</span>
                          </td>
                          <td className="px-3 py-2 text-sm text-on-surface-variant">{compactHours(row.plannedHours)}</td>
                          <td className="px-3 py-2 text-sm text-on-surface-variant">
                            {formatDate(row.plannedStart)} - {formatDate(row.plannedEnd)}
                          </td>
                        </tr>
                      ))
                    ) : (
                      <tr>
                        <td colSpan={6} className="px-3 py-5 text-sm text-on-surface-variant">No jobs found.</td>
                      </tr>
                    )}
                  </tbody>
                </table>
              </div>
              <div className="mt-4 flex items-center justify-between gap-4">
                <span className="text-xs text-on-surface-variant">{jobRowLabel}</span>
                <AppPagination
                  page={jobPage}
                  totalPages={jobTotalPages}
                  onPageChange={setJobPage}
                  onPrevious={() => setJobPage((prev) => Math.max(1, prev - 1))}
                  onNext={() => setJobPage((prev) => Math.min(jobTotalPages, prev + 1))}
                  canPrevious={jobPage > 1}
                  canNext={jobPage < jobTotalPages}
                />
              </div>
            </section>

            {/* Timesheets Section */}
            <section className="rounded-2xl bg-surface-container-lowest p-4">
              <div className="flex items-center gap-2">
                <h2 className="text-lg font-extrabold text-on-surface">Timesheets</h2>
                {timesheetsLoading && (
                  <span className="text-xs text-on-surface-variant">(loading...)</span>
                )}
              </div>
              <div className="mt-3 overflow-x-auto">
                <table className="w-full min-w-[900px] border-collapse text-left">
                  <thead>
                    <tr className="border-b border-surface-container">
                      <th className="px-3 py-2 text-xs font-bold uppercase tracking-[0.12em] text-on-surface-variant">Date</th>
                      <th className="px-3 py-2 text-xs font-bold uppercase tracking-[0.12em] text-on-surface-variant">Employee</th>
                      <th className="px-3 py-2 text-xs font-bold uppercase tracking-[0.12em] text-on-surface-variant">Job</th>
                      <th className="px-3 py-2 text-xs font-bold uppercase tracking-[0.12em] text-on-surface-variant">Hours</th>
                      <th className="px-3 py-2 text-xs font-bold uppercase tracking-[0.12em] text-on-surface-variant">Status</th>
                      <th className="px-3 py-2 text-xs font-bold uppercase tracking-[0.12em] text-on-surface-variant">Notes</th>
                    </tr>
                  </thead>
                  <tbody className="divide-y divide-surface-container/70">
                    {pagedTimesheets.length ? (
                      pagedTimesheets.map((ts) => {
                        const dateVal = ts.entryDate || ts.entry_date || ts.date || ts.work_date
                        const empVal = ts.userName || ts.user_name || ts.employee_name || ts.employeeName || (assigneeNameById.get(Number(ts.userId || ts.user_id))) || '-'
                        const jobVal = ts.jobName || ts.job_name || ts.taskName || ts.task_name || '-'
                        const hoursVal = ts.hoursLogged ?? ts.hours_logged
                        const statusVal = ts.approvalStatus || ts.approval_status || ts.status || '-'
                        const notesVal = ts.remarks || ts.activityDescription || ts.notes || ts.description || '-'

                        return (
                          <tr key={ts.id}>
                            <td className="px-3 py-2 text-sm text-on-surface-variant">{formatDate(dateVal)}</td>
                            <td className="px-3 py-2 text-sm font-semibold text-on-surface">{empVal}</td>
                            <td className="px-3 py-2 text-sm text-on-surface-variant">{jobVal}</td>
                            <td className="px-3 py-2 text-sm font-semibold text-on-surface">{compactHours(hoursVal)}</td>
                            <td className="px-3 py-2">
                              <span className={`rounded-full px-2 py-1 text-[10px] font-bold uppercase ${badgeClass(statusVal)}`}>
                                {String(statusVal).replaceAll('_', ' ')}
                              </span>
                            </td>
                            <td className="max-w-[200px] truncate px-3 py-2 text-xs text-on-surface-variant" title={notesVal !== '-' ? notesVal : undefined}>
                              {notesVal}
                            </td>
                          </tr>
                        )
                      })
                    ) : (
                      <tr>
                        <td colSpan={6} className="px-3 py-5 text-sm text-on-surface-variant">
                          {timesheetsLoading ? 'Loading timesheets...' : 'No timesheets found.'}
                        </td>
                      </tr>
                    )}
                  </tbody>
                </table>
              </div>
              <div className="mt-4 flex items-center justify-between gap-4">
                <span className="text-xs text-on-surface-variant">{timesheetRowLabel}</span>
                <AppPagination
                  page={timesheetPage}
                  totalPages={timesheetTotalPages}
                  onPageChange={setTimesheetPage}
                  onPrevious={() => setTimesheetPage((prev) => Math.max(1, prev - 1))}
                  onNext={() => setTimesheetPage((prev) => Math.min(timesheetTotalPages, prev + 1))}
                  canPrevious={timesheetPage > 1}
                  canNext={timesheetPage < timesheetTotalPages}
                />
              </div>
            </section>
          </div>
        ) : (
          <div className="space-y-6">
            <article className="rounded-2xl border border-outline-variant/20 bg-surface-container-lowest p-6 shadow-sm">
              <div className="flex flex-col gap-4 sm:flex-row sm:items-center sm:justify-between">
                <div>
                  <h3 className="text-lg font-bold text-on-surface">Timeframe Filter</h3>
                  <p className="text-xs text-on-surface-variant">Prorate AMC revenue and filter timesheet costs by date range</p>
                </div>

                <div className="flex flex-wrap items-center gap-3">
                  {/* Preset Buttons — no CUSTOM tab */}
                  <div className="flex rounded-lg p-1 w-full justify-evenly bg-surface-container-low">
                    {['ALL', 'THIS_YEAR', 'LAST_YEAR', 'LAST_6_MONTHS'].map((p) => (
                      <button
                        key={p}
                        type="button"
                        onClick={() => handlePresetChange(p)}
                        className={`rounded-md px-3 py-1.5 text-xs font-bold transition-all ${preset === p
                          ? 'bg-primary text-on-primary shadow-sm'
                          : 'text-on-surface-variant hover:text-on-surface'
                          }`}
                      >
                        {p.replace(/_/g, ' ')}
                      </button>
                    ))}
                  </div>

                  {/* Always-visible date range inputs */}
                  <div className="flex items-center gap-2 w-full justify-around">
                    <span className="text-[10px] font-bold uppercase tracking-wider text-on-surface-variant">From</span>
                    <AppDateInput
                      value={fromDate}
                      onChange={handleFromDateChange}
                      className="w-36"
                      inputClassName="rounded-lg border-none bg-surface-container-low px-3 py-1.5 text-xs text-on-surface outline-none focus:ring-2 focus:ring-primary/30"
                    />
                    <span className="text-[10px] font-bold uppercase tracking-wider text-on-surface-variant">To</span>
                    <AppDateInput
                      value={toDate}
                      onChange={handleToDateChange}
                      className="w-36"
                      inputClassName="rounded-lg border-none bg-surface-container-low px-3 py-1.5 text-xs text-on-surface outline-none focus:ring-2 focus:ring-primary/30"
                    />

                    {/* Recalculate button */}
                    <button
                      type="button"
                      onClick={handleRecalculate}
                      disabled={profitabilityLoading}
                      className="inline-flex items-center gap-1.5 rounded-lg bg-primary/10 px-4 py-2 text-xs font-bold text-primary transition hover:bg-primary/20 disabled:opacity-50"
                    >
                      <span className="material-symbols-outlined text-sm">refresh</span>
                      Recalculate
                    </button>

                  </div>

                </div>
              </div>
            </article>

            {profitabilityLoading ? (
              <div className="rounded-2xl border border-outline-variant/10 bg-surface-container-lowest px-6 py-16 text-center text-sm text-on-surface-variant">
                Loading profitability statistics...
              </div>
            ) : profitabilityError ? (
              <div className="rounded-2xl border border-error/30 bg-error-container p-4 text-sm text-error">
                {profitabilityError}
              </div>
            ) : profitability ? (
              <>
                {/* warning banner for missing CTC */}
                {profitability.rollup?.isPartial && (
                  <div className="flex gap-3 rounded-lg bg-amber-500/10 p-4 border border-amber-500/20 text-amber-800 text-xs">
                    <span className="material-symbols-outlined text-amber-600">warning</span>
                    <div>
                      <p className="font-bold">Partial Cost Rollup</p>
                      <p className="mt-0.5 opacity-90">
                        Some timesheets within this period lack employee hourly CTC.
                        The logged hours are counted in total hours but $0 is attributed to cost, which might underestimate the total cost.
                      </p>
                    </div>
                  </div>
                )}

                {/* KPI Tiles Row */}
                <div className="grid grid-cols-2 gap-4 sm:grid-cols-3 lg:grid-cols-6">
                  <div className="rounded-xl bg-surface-container-lowest border border-outline-variant/15 p-5 shadow-sm transition hover:shadow-md">
                    <p className="text-[10px] font-bold uppercase tracking-wider text-on-surface-variant">Total Revenue</p>
                    <div className="mt-2 text-xl font-extrabold text-on-surface">
                      <SecureField privilege="ProjectFinancialsView">
                        {project.project_amc_currency || 'INR'} {Number(profitability.rollup?.totalRevenue || 0).toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })}
                      </SecureField>
                    </div>
                    <span className="text-[9px] text-on-surface-variant opacity-70">
                      AMC/Fixed Budget + sub-projects
                    </span>
                  </div>

                  <div className="rounded-xl bg-surface-container-lowest border border-outline-variant/15 p-5 shadow-sm transition hover:shadow-md">
                    <p className="text-[10px] font-bold uppercase tracking-wider text-on-surface-variant">Salary Cost</p>
                    <p className="mt-2 text-xl font-extrabold text-on-surface">
                      {project.project_amc_currency || 'INR'} {Number(profitability.rollup?.totalCost || 0).toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })}
                    </p>
                    <span className="text-[9px] text-on-surface-variant opacity-70">
                      Timesheets CTC
                    </span>
                  </div>

                  <div className="rounded-xl bg-surface-container-lowest border border-outline-variant/15 p-5 shadow-sm transition hover:shadow-md">
                    <p className="text-[10px] font-bold uppercase tracking-wider text-on-surface-variant">Net Profit</p>
                    <div className={`mt-2 text-xl font-extrabold ${(profitability.rollup?.profitabilityAmount || 0) >= 0 ? 'text-green-600' : 'text-error'}`}>
                      <SecureField privilege="ProjectFinancialsView">
                        {project.project_amc_currency || 'INR'} {Number(profitability.rollup?.profitabilityAmount || 0).toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })}
                      </SecureField>
                    </div>
                    <span className="text-[9px] text-on-surface-variant opacity-70">
                      Revenue - Cost
                    </span>
                  </div>

                  <div className="rounded-xl bg-surface-container-lowest border border-outline-variant/15 p-5 shadow-sm transition hover:shadow-md">
                    <p className="text-[10px] font-bold uppercase tracking-wider text-on-surface-variant">Engineering Hours</p>
                    <p className="mt-2 text-xl font-extrabold text-on-surface">
                      {Number((profitability?.project?.totalHoursLogged || 0) + (profitability?.project?.subProjects?.reduce((acc, sp) => acc + (sp.totalHoursLogged || 0), 0) || 0)).toFixed(1)} hrs
                    </p>
                    <span className="text-[9px] text-on-surface-variant opacity-70">
                      Approved timesheets
                    </span>
                  </div>

                  <div className="rounded-xl bg-surface-container-lowest border border-outline-variant/15 p-5 shadow-sm transition hover:shadow-md">
                    <p className="text-[10px] font-bold uppercase tracking-wider text-on-surface-variant">Synced Tickets</p>
                    <p className="mt-2 text-xl font-extrabold text-on-surface">
                      {Number(ticketMetrics.totalTickets || 0)}
                    </p>
                    <span className="text-[9px] text-on-surface-variant opacity-70">
                      ERP webhook sync
                    </span>
                  </div>

                  <div className="rounded-xl bg-surface-container-lowest border border-outline-variant/15 p-5 shadow-sm transition hover:shadow-md">
                    <p className="text-[10px] font-bold uppercase tracking-wider text-on-surface-variant">Feedback Events</p>
                    <p className="mt-2 text-xl font-extrabold text-on-surface">
                      {Number(ticketMetrics.feedbackEvents || 0)}
                    </p>
                    <span className="text-[9px] text-on-surface-variant opacity-70">
                      TicketSync feedback
                    </span>
                  </div>
                </div>

                <div className="grid grid-cols-1 gap-6 lg:grid-cols-12">
                  {/* Left Column: Gauge & breakdowns */}
                  <div className="flex flex-col gap-6 lg:col-span-8">
                    {/* Profitability Gauge */}
                    <article className="rounded-2xl border border-outline-variant/15 bg-surface-container-lowest p-6 shadow-sm">
                      <h3 className="text-base font-extrabold text-on-surface mb-4">Profitability Gauge</h3>
                      <div className="flex flex-col items-center gap-6 sm:flex-row sm:justify-around">
                        <div className="relative flex h-32 w-32 items-center justify-center rounded-full border-8 border-surface-container-high">
                          <div className={`absolute inset-0 rounded-full border-8 border-t-8 border-r-8 ${profitability.rollup?.profitabilityPct === null || profitability.rollup?.profitabilityPct === undefined
                            ? 'border-slate-400'
                            : profitability.rollup.profitabilityPct >= 30
                              ? 'border-green-500'
                              : profitability.rollup.profitabilityPct >= 10
                                ? 'border-amber-500'
                                : 'border-error'
                            }`} />
                          <div className="text-center z-10">
                            <span className="text-2xl font-black text-on-surface">
                              {!canViewFinancials ? (
                                <span className="text-xs font-semibold text-on-surface-variant/60">Restricted</span>
                              ) : profitability.rollup?.profitabilityPct === null || profitability.rollup?.profitabilityPct === undefined ? (
                                'N/A'
                              ) : (
                                `${profitability.rollup.profitabilityPct.toFixed(1)}%`
                              )}
                            </span>
                            <p className="text-[9px] font-bold text-on-surface-variant uppercase tracking-wider">Margin</p>
                          </div>
                        </div>

                        <div className="space-y-3 max-w-sm">
                          <div className="flex items-center gap-2.5">
                            <span className={`h-3 w-3 rounded-full ${(profitability.rollup?.profitabilityPct ?? 0) >= 30 ? 'bg-green-500 ring-4 ring-green-500/20' : 'bg-green-300'
                              }`} />
                            <div>
                              <p className="text-xs font-bold text-on-surface">Excellent Margin (&gt;= 30%)</p>
                              <p className="text-[10px] text-on-surface-variant">Highly profitable client/delivery scope</p>
                            </div>
                          </div>
                          <div className="flex items-center gap-2.5">
                            <span className={`h-3 w-3 rounded-full ${(profitability.rollup?.profitabilityPct ?? 0) >= 10 && (profitability.rollup?.profitabilityPct ?? 0) < 30
                              ? 'bg-amber-500 ring-4 ring-amber-500/20' : 'bg-amber-300'
                              }`} />
                            <div>
                              <p className="text-xs font-bold text-on-surface">Acceptable Margin (10% - 30%)</p>
                              <p className="text-[10px] text-on-surface-variant">Average yield, monitor effort slippage</p>
                            </div>
                          </div>
                          <div className="flex items-center gap-2.5">
                            <span className={`h-3 w-3 rounded-full ${profitability.rollup?.profitabilityPct !== null && (profitability.rollup?.profitabilityPct ?? 0) < 10
                              ? 'bg-error ring-4 ring-error/20' : 'bg-error/30'
                              }`} />
                            <div>
                              <p className="text-xs font-bold text-on-surface">Low Margin (&lt; 10%)</p>
                              <p className="text-[10px] text-on-surface-variant">High cost-to-revenue ratio, action needed</p>
                            </div>
                          </div>
                        </div>
                      </div>
                    </article>

                    {/* Department Effort Breakdown */}
                    <article className="rounded-2xl border border-outline-variant/15 bg-surface-container-lowest p-6 shadow-sm">
                      <h3 className="text-base font-extrabold text-on-surface mb-4">Effort Contribution Breakdown</h3>

                      {/* Bar Visualizer */}
                      <div className="space-y-4">
                        {departmentEffort.length > 0 ? (
                          departmentEffort.slice(0, 5).map((dept, idx) => {
                            const totalHours = departmentEffort.reduce((sum, item) => sum + Number(item.hoursLogged || 0), 0) || 1
                            const hours = Number(dept.hoursLogged || 0)
                            const pct = Math.max(5, Math.round((hours / totalHours) * 100))
                            const colors = ['bg-primary', 'bg-tertiary', 'bg-green-600', 'bg-blue-600', 'bg-purple-600']
                            const barColor = colors[idx % colors.length]
                            return (
                              <div key={dept.departmentId || dept.departmentName || idx} className="space-y-1">
                                <div className="flex justify-between text-xs font-bold">
                                  <span className="text-on-surface">{dept.departmentName || 'Unassigned'} ({dept.employeeCount || 0} people)</span>
                                  <span className="text-on-surface-variant">{hours.toFixed(1)} hrs ({pct}%)</span>
                                </div>
                                <div className="h-2.5 w-full rounded-full bg-surface-container-high overflow-hidden">
                                  <div className={`h-full rounded-full ${barColor}`} style={{ width: `${pct}%` }} />
                                </div>
                              </div>
                            )
                          })
                        ) : (
                          <p className="text-xs text-on-surface-variant text-center py-4">No approved timesheets found in this period to aggregate.</p>
                        )}
                      </div>
                    </article>

                    {/* Expandable Employee Cost Table */}
                    <article className="rounded-2xl border border-outline-variant/15 bg-surface-container-lowest p-6 shadow-sm">
                      <h3 className="text-base font-extrabold text-on-surface mb-4">Employee Timesheet Cost Breakdown</h3>
                      <div className="overflow-x-auto">
                        <table className="w-full border-collapse text-left text-sm">
                          <thead>
                            <tr className="border-b border-outline-variant/30 text-xs font-bold uppercase tracking-wider text-on-surface-variant">
                              <th 
                                className="pb-3 pr-4 cursor-pointer select-none hover:text-primary transition"
                                onClick={() => handleSort('name')}
                              >
                                Employee {sortField === 'name' ? (sortDirection === 'asc' ? ' ▲' : ' ▼') : ''}
                              </th>
                              <th 
                                className="pb-3 px-4 text-center cursor-pointer select-none hover:text-primary transition"
                                onClick={() => handleSort('hoursLogged')}
                              >
                                Hours Logged {sortField === 'hoursLogged' ? (sortDirection === 'asc' ? ' ▲' : ' ▼') : ''}
                              </th>
                              <th className="pb-3 px-4 text-right">CTC Rate</th>
                              <th 
                                className="pb-3 px-4 text-right cursor-pointer select-none hover:text-primary transition"
                                onClick={() => handleSort('employeeCost')}
                              >
                                Estimated Cost {sortField === 'employeeCost' ? (sortDirection === 'asc' ? ' ▲' : ' ▼') : ''}
                              </th>
                              <th className="pb-3 pl-4 text-center">Action</th>
                            </tr>
                          </thead>
                          <tbody>
                            {sortedEmployeeBreakdown?.length > 0 ? (
                              sortedEmployeeBreakdown.map((emp) => {
                                const isExpanded = expandedEmployeeId === emp.userId
                                return (
                                  <Fragment key={emp.userId}>
                                    <tr className="border-b border-outline-variant/10 hover:bg-surface-container-low/30 transition">
                                      <td className="py-4 pr-4">
                                        <p className="font-semibold text-on-surface">{emp.name}</p>
                                        <p className="text-[10px] text-on-surface-variant font-mono">{emp.employeeCode}</p>
                                      </td>
                                      <td className="py-4 px-4 text-center font-semibold tabular-nums">
                                        {emp.hoursLogged.toFixed(1)} h
                                      </td>
                                      <td className="py-4 px-4 text-right tabular-nums">
                                        {emp.isMissingCtc ? (
                                          <span className="text-amber-600 font-bold text-xs" title="Missing CTC">N/A</span>
                                        ) : profitability.project?.canViewCtcRates && typeof emp.ctcPerHour === 'number' ? (
                                          <span>{project.project_amc_currency || 'INR'} {emp.ctcPerHour.toFixed(2)}/hr</span>
                                        ) : (
                                          <span className="text-xs font-bold text-on-surface-variant">Restricted</span>
                                        )}
                                      </td>
                                      <td className="py-4 px-4 text-right font-bold tabular-nums text-on-surface">
                                        {project.project_amc_currency || 'INR'} {emp.employeeCost.toLocaleString(undefined, { minimumFractionDigits: 2 })}
                                      </td>
                                      <td className="py-4 pl-4 text-center">
                                        <button
                                          type="button"
                                          onClick={() => setExpandedEmployeeId(isExpanded ? null : emp.userId)}
                                          className="text-primary hover:text-primary/80"
                                        >
                                          <span className="material-symbols-outlined text-base">
                                            {isExpanded ? 'expand_less' : 'expand_more'}
                                          </span>
                                        </button>
                                      </td>
                                    </tr>
                                    {isExpanded && (
                                      <tr key={`${emp.userId}-details`}>
                                        <td colSpan={5} className="bg-surface-container-low/30 px-4 py-3 text-xs">
                                          <div className="space-y-1.5 p-2 rounded bg-surface-container-lowest">
                                            <p className="font-bold text-on-surface mb-2">Logged Hours Details</p>
                                            <div className="flex justify-between border-b border-outline-variant/20 pb-1 text-[10px] text-on-surface-variant uppercase font-bold">
                                              <span>Activity / Task Type</span>
                                              <span>Hours</span>
                                            </div>
                                            {/* Timesheet jobs/tasks drilldown */}
                                            {(() => {
                                              const empTimesheets = allTimesheets.filter(ts => Number(ts.userId || ts.user_id) === Number(emp.userId))
                                              if (empTimesheets.length > 0) {
                                                return empTimesheets.map(ts => (
                                                  <div key={ts.id} className="flex justify-between py-1 border-b border-outline-variant/10 text-on-surface-variant font-medium">
                                                    <span>{ts.jobName || ts.taskName || ts.activityDescription || 'Logged Hours'} ({formatDate(ts.entryDate || ts.entry_date)})</span>
                                                    <span className="font-mono">{compactHours(ts.hoursLogged ?? ts.hours_logged)}</span>
                                                  </div>
                                                ))
                                              }
                                              const empJobs = allJobs.filter(j => j.assigneeUserId === emp.userId)
                                              if (empJobs.length > 0) {
                                                return empJobs.map(j => (
                                                  <div key={j.id} className="flex justify-between py-1 border-b border-outline-variant/10 text-on-surface-variant font-medium">
                                                    <span>{j.jobName} ({j.jobCode})</span>
                                                    <span className="font-mono">{j.plannedHours || 0} h</span>
                                                  </div>
                                                ))
                                              }
                                              return <p className="text-on-surface-variant opacity-70 text-[10px]">No linked jobs or timesheets found for this employee.</p>
                                            })()}
                                          </div>
                                        </td>
                                      </tr>
                                    )}
                                  </Fragment>
                                )
                              })
                            ) : (
                              <tr>
                                <td colSpan={5} className="py-8 text-center text-xs text-on-surface-variant">
                                  No approved timesheet data found.
                                </td>
                              </tr>
                            )}
                          </tbody>
                        </table>
                      </div>
                    </article>

                    {/* Sub-projects profitability list */}
                    {profitability.project?.subProjects?.length > 0 && (
                      <article className="rounded-2xl border border-outline-variant/15 bg-surface-container-lowest p-6 shadow-sm">
                        <h3 className="text-base font-extrabold text-on-surface mb-4">Sub-Projects Profitability</h3>
                        <div className="overflow-x-auto">
                          <table className="w-full border-collapse text-left text-sm">
                            <thead>
                              <tr className="border-b border-outline-variant/30 text-xs font-bold uppercase tracking-wider text-on-surface-variant">
                                <th className="pb-3 pr-4">Sub-Project</th>
                                <th className="pb-3 px-4 text-right">Revenue</th>
                                <th className="pb-3 px-4 text-right">Cost</th>
                                <th className="pb-3 px-4 text-right">Profit</th>
                                <th className="pb-3 pl-4 text-center">Margin %</th>
                              </tr>
                            </thead>
                            <tbody>
                              {profitability.project.subProjects.map((sp) => (
                                <tr key={sp.projectId} className="border-b border-outline-variant/10 hover:bg-surface-container-low/30 transition">
                                  <td className="py-4 pr-4">
                                    <p className="font-semibold text-primary hover:underline">
                                      <a href={`/nerve/project/detail?id=${sp.projectId}`}>{sp.projectName}</a>
                                    </p>
                                    <p className="text-[10px] text-on-surface-variant font-mono">{sp.projectCode}</p>
                                  </td>
                                  <td className="py-4 px-4 text-right font-semibold tabular-nums">
                                    {project.project_amc_currency || 'INR'} {sp.totalRevenue.toLocaleString(undefined, { minimumFractionDigits: 2 })}
                                  </td>
                                  <td className="py-4 px-4 text-right tabular-nums">
                                    {project.project_amc_currency || 'INR'} {sp.totalCost.toLocaleString(undefined, { minimumFractionDigits: 2 })}
                                  </td>
                                  <td className={`py-4 px-4 text-right font-bold tabular-nums ${(sp.profitabilityAmount || 0) >= 0 ? 'text-green-600' : 'text-error'}`}>
                                    {project.project_amc_currency || 'INR'} {sp.profitabilityAmount.toLocaleString(undefined, { minimumFractionDigits: 2 })}
                                  </td>
                                  <td className="py-4 pl-4 text-center">
                                    <span className={`inline-flex rounded-full px-2.5 py-0.5 text-xs font-bold ${sp.profitabilityPct === null
                                      ? 'bg-gray-100 text-gray-700'
                                      : sp.profitabilityPct >= 30
                                        ? 'bg-green-100 text-green-800'
                                        : sp.profitabilityPct >= 10
                                          ? 'bg-amber-100 text-amber-800'
                                          : 'bg-red-100 text-red-800'
                                      }`}>
                                      {sp.profitabilityPct === null ? 'N/A' : `${sp.profitabilityPct.toFixed(1)}%`}
                                    </span>
                                  </td>
                                </tr>
                              ))}
                            </tbody>
                          </table>
                        </div>
                      </article>
                    )}
                  </div>

                  {/* Right Column: Touchpoints Timeline */}
                  <div className="flex flex-col gap-6 lg:col-span-4">
                    <article className="rounded-2xl border border-outline-variant/15 bg-surface-container-lowest p-6 shadow-sm flex-1">
                      <h3 className="text-base font-extrabold text-on-surface mb-6">Touchpoints Timeline</h3>

                      <div className="relative border-l-2 border-outline-variant/40 pl-6 space-y-6">
                        {timelineEvents.slice(0, 15).map((evt) => {
                          const isFeedback = evt.type === 'CUSTOMER_FEEDBACK'
                          const isOpen = evt.type === 'TICKET_OPENED'

                          return (
                            <div key={evt.id} className="relative">
                              {/* Circle Icon Indicator */}
                              <span className={`absolute -left-[31px] top-0.5 flex h-3.5 w-3.5 items-center justify-center rounded-full ring-4 ring-white ${isFeedback ? 'bg-amber-500' : isOpen ? 'bg-primary' : 'bg-secondary'
                                }`} />

                              <div>
                                <span className="text-[10px] font-bold text-on-surface-variant/70 block uppercase tracking-wider">
                                  {formatDate(evt.date)}
                                </span>
                                <p className="text-sm font-bold text-on-surface mt-0.5">{evt.title}</p>
                                <div className="mt-1.5 flex flex-wrap gap-1.5 items-center">
                                  <span className="font-mono text-[9px] bg-surface-container-high px-1.5 py-0.5 rounded text-on-surface-variant">
                                    {evt.code}
                                  </span>
                                  {evt.priority && (
                                    <span className={`text-[9px] font-bold px-1.5 py-0.5 rounded ${evt.priority === 'HIGH' ? 'bg-red-100 text-red-800' : 'bg-gray-100 text-gray-800'
                                      }`}>
                                      {evt.priority}
                                    </span>
                                  )}
                                  {evt.user && (
                                    <span className="text-[9px] font-semibold text-on-surface-variant">
                                      by {evt.user}
                                    </span>
                                  )}
                                </div>
                              </div>
                            </div>
                          )
                        })}

                        {timelineEvents.length === 0 && (
                          <p className="text-xs text-on-surface-variant opacity-70">No ticket events synced for this project timeline.</p>
                        )}
                      </div>
                    </article>
                  </div>
                </div>
              </>
            ) : (
              <div className="rounded-2xl border border-outline-variant/10 bg-surface-container-lowest px-6 py-16 text-center text-sm text-on-surface-variant">
                No profitability records found.
              </div>
            )}
          </div>
        )
      ) : null}

      {/* Receive Payment Modal */}
      {payModalSchedule && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50 backdrop-blur-sm p-4">
          <div className="w-full max-w-md rounded-2xl bg-surface-container-lowest p-6 shadow-2xl border border-outline-variant/30">
            <div className="flex items-center justify-between border-b border-outline-variant/20 pb-4">
              <div className="flex items-center gap-2">
                <div className="flex h-10 w-10 items-center justify-center rounded-xl bg-emerald-100 text-emerald-700">
                  <span className="material-symbols-outlined">payments</span>
                </div>
                <div>
                  <h3 className="text-lg font-bold text-on-surface">Receive Payment</h3>
                  <p className="text-xs text-on-surface-variant">Cycle #{payModalSchedule.cycle_number} • {formatDate(payModalSchedule.period_start_date)} - {formatDate(payModalSchedule.period_end_date)}</p>
                </div>
              </div>
              <button
                type="button"
                onClick={() => setPayModalSchedule(null)}
                className="text-on-surface-variant hover:text-on-surface"
              >
                <span className="material-symbols-outlined">close</span>
              </button>
            </div>

            <form onSubmit={handleSubmitReceivePayment} className="mt-5 space-y-4">
              <div>
                <label className="block text-xs font-bold uppercase tracking-wider text-on-surface-variant mb-1">
                  Amount Received * ({project.project_amc_currency || 'INR'})
                </label>
                <input
                  type="number"
                  step="0.01"
                  required
                  value={payAmount}
                  onChange={(e) => setPayAmount(e.target.value)}
                  className="w-full rounded-xl border border-outline px-3.5 py-2.5 text-sm font-bold text-on-surface bg-surface-container-low focus:border-primary focus:outline-none"
                />
              </div>

              <div>
                <label className="block text-xs font-bold uppercase tracking-wider text-on-surface-variant mb-1">
                  Payment Date *
                </label>
                <input
                  type="date"
                  required
                  value={payDate}
                  onChange={(e) => setPayDate(e.target.value)}
                  className="w-full rounded-xl border border-outline px-3.5 py-2.5 text-sm text-on-surface bg-surface-container-low focus:border-primary focus:outline-none"
                />
              </div>

              <div>
                <label className="block text-xs font-bold uppercase tracking-wider text-on-surface-variant mb-1">
                  Reference / Payment Notes
                </label>
                <textarea
                  rows={2}
                  placeholder="e.g. Bank Transfer TXN-98765"
                  value={payNotes}
                  onChange={(e) => setPayNotes(e.target.value)}
                  className="w-full rounded-xl border border-outline px-3.5 py-2.5 text-sm text-on-surface bg-surface-container-low focus:border-primary focus:outline-none"
                />
              </div>

              <div className="flex items-center justify-end gap-3 pt-3">
                <button
                  type="button"
                  onClick={() => setPayModalSchedule(null)}
                  className="rounded-xl border border-outline px-4 py-2 text-xs font-bold text-on-surface hover:bg-surface-container-high"
                >
                  Cancel
                </button>
                <button
                  type="submit"
                  disabled={isSubmittingPay}
                  className="inline-flex items-center gap-1.5 rounded-xl bg-emerald-600 px-5 py-2 text-xs font-bold text-white hover:bg-emerald-700 disabled:opacity-50 shadow-md"
                >
                  {isSubmittingPay ? 'Recording...' : 'Submit Payment'}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}
    </div>
  )
}
